import { useState, useRef, useEffect } from "react";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { trackBookingConversion, BOOKING_URL } from "@/lib/trackConversion";

const servicePages = [
  { label: "Dental Associates", href: "/dental-associates" },
  { label: "Dental Therapists", href: "/dental-therapists" },
  { label: "Dental Hygienists", href: "/dental-hygienists" },
  { label: "Practice Owners", href: "/practice-owners" },
  { label: "Buying a Practice", href: "/buying-a-dental-practice" },
];

const navLinks = [
  { label: "About", href: "/about" },
  { label: "Services", href: "/#services", hasDropdown: true },
  { label: "How It Works", href: "/#how-it-works" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/#contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border/60">
      <div className="container mx-auto flex items-center justify-between gap-6 px-4 sm:px-6 lg:px-8 h-[80px] md:h-[96px]">
        <a href="/" aria-label="RR Dental Accountants, home" className="flex items-center justify-center group shrink-0 h-full">
          <div className="relative flex items-center justify-center h-full">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <img src="/rr-dental-logo.png" alt="RR Dental Accountants" className="relative block h-12 sm:h-14 md:h-16 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-transform duration-300 group-hover:scale-105" width={320} height={179} decoding="async" loading="eager" />
          </div>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.href} className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center gap-1 text-sm font-medium text-foreground/70 hover:text-primary transition-colors"
                >
                  {link.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {servicesOpen ? (
                  <div
                    className="absolute top-full left-0 mt-2 w-52 bg-card border border-border rounded-xl overflow-hidden z-50 animate-in fade-in-0 zoom-in-95 duration-200"
                    style={{ boxShadow: "var(--shadow-card-hover)" }}
                  >
                      <a
                        href={link.href}
                        className="block px-4 py-2.5 text-sm text-foreground/70 hover:bg-muted hover:text-primary transition-colors font-medium"
                        onClick={() => setServicesOpen(false)}
                      >
                        All Services
                      </a>
                      {servicePages.map((page) => (
                        <Link
                          key={page.href}
                          to={page.href}
                          className="block px-4 py-2.5 text-sm text-foreground/70 hover:bg-muted hover:text-primary transition-colors"
                          onClick={() => setServicesOpen(false)}
                        >
                          {page.label}
                        </Link>
                      ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            )
          )}
          <a
            href={BOOKING_URL}
            onClick={trackBookingConversion}
            className="inline-flex items-center gap-2 text-primary-foreground px-6 py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-lg hover:scale-[1.02]"
            style={{ background: "var(--gradient-cta)" }}
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg text-foreground hover:bg-muted active:bg-muted/80 transition-colors shrink-0"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open ? (
        <div
            id="mobile-menu"
            className="md:hidden bg-card border-b border-border overflow-hidden shadow-lg animate-in slide-in-from-top-2 fade-in-0 duration-200"
          >
            <div className="flex flex-col p-4 max-h-[calc(100vh-96px)] overflow-y-auto">
              {navLinks.map((link) => (
                <div key={link.href} className="border-b border-border/40">
                  <a
                    href={link.href}
                    className="flex items-center min-h-[48px] py-3 px-2 text-base text-foreground hover:text-primary transition-colors font-medium"
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </a>
                  {link.hasDropdown && (
                    <div className="ml-4 pb-2 flex flex-col">
                      {servicePages.map((page) => (
                        <Link
                          key={page.href}
                          to={page.href}
                          className="flex items-center min-h-[40px] py-2 px-2 text-sm text-foreground/70 hover:text-primary transition-colors"
                          onClick={() => setOpen(false)}
                        >
                          {page.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={BOOKING_URL}
                className="mt-4 inline-flex items-center justify-center gap-2 text-primary-foreground px-6 py-3 rounded-xl text-base font-semibold min-h-[48px]"
                style={{ background: "var(--gradient-cta)" }}
                onClick={() => { trackBookingConversion(); setOpen(false); }}
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
        </div>
      ) : null}
    </nav>
  );
};

export default Navbar;
